<template lang="pug">
component(
  v-if="layout"
  :is="layout"
  :page="page")
</template>

<script lang="ts">
import { mapActions, mapGetters } from "vuex"
import { replaceUrlS3 } from "~/utils/imageUrlS3"
import { SET_BREADCRUMBS } from "~/store/pages"
import { SET_FW_TOKEN, SET_USER, GET_FW_TOKEN } from "~/store/auth"
import { AuthenticationService } from "~/services/public/Authentication"
import { TrackingService } from "~/services/public/Tracking"
import { PhoneNumberService } from "~/services/public/PhoneNumber"
import { CreditCodeService } from "~/services/public/CreditCode"
import { SET_CREDIT_CODE_VALUE } from "~/store/credit-code"
import { SET_PHONE_NUMBER_VALUE } from "~/store/phone-number"
import { SET_LOCATION_INFO, SET_MAP_LOCATIONS } from "~/store/location"
import { SET_INTERNAL_REVIEWS } from "~/store/internal-reviews"
import { buildStructuredData } from "~/scripts/google-structured-data"
import {
  getHrefLangLinkMetaForHomePage,
  getHrefLangLinkMetaForTPPage,
  getPath,
  isHomePage,
  isTPPage
} from "~/scripts/useful-functions"
import { PageLayoutRebrand } from "#components"
import type { LinkPropertyHref } from "vue-meta/types/vue-meta"
import { LOCALE } from "~/constants/locale"

interface ComponentData {
  layout: string
  internalReviewsFilter: Record<string, any>
}

type RobotsMeta = "noindex" | "nofollow"

interface PageLayoutProvider {
  internalReviewsFilter: Record<string, any>
  layout: string
  setBreadcrumbs: (payload: { breadcrumbs: any }) => void
  replaceUrlS3: (url: string, config: any) => string
  setMapLocations: (location: any) => void
  setLocationInfo: (location: any) => void
  setInternalReviews: (reviews: any) => void
  setFwToken: (token: String) => void
  setUser: (user: any) => void
  setPhoneNumberValue: (phoneNumber: any) => void
  setCreditCodeValue: (creditCode: number) => void
  getLayoutFromTemplate: () => string
  authenticateAndUpdatePhone: () => Promise<void>
  updatePhoneNumber: () => Promise<void>
  useCreditCode: () => Promise<void>
  sendTrackingEvents: () => Promise<void>
}

export default defineComponent({
  name: "PageLayout",
  components: {
    PageLayoutRebrand
  },

  provide() {
    return {
      internalReviewsFilter: this.internalReviewsFilter
    }
  },

  props: {
    page: {
      type: Object as () => Page,
      required: true
    },
    noSeo: {
      type: Boolean
    }
  },

  setup(props) {
    const route = useRoute()
    const i18n = useI18n()
    const config = useRuntimeConfig()

    const headConfig = computed(() => {
      const head: MetaInfo = { script: [] as any[] }

      if (config.public.oneTrustAccountId) {
        Object.assign(head, { __dangerouslyDisableSanitizers: ["innerHTML"] })

        let oneTrustLanguage = (i18n.locale.value as AnyVanLocale) || LOCALE.UK
        if (oneTrustLanguage === LOCALE.US) {
          oneTrustLanguage = LOCALE.UK
        }

        head.script.push(
          {
            innerHTML: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){
              dataLayer.push(arguments);
            }
            gtag("consent", "default", {"ad_storage": "granted", "analytics_storage": "granted"});`
          },
          {
            src: "https://cdn-ukwest.onetrust.com/scripttemplates/otSDKStub.js",
            "data-domain-script": config.public.oneTrustAccountId,
            async: true,
            "data-language": oneTrustLanguage
          }
        )
      }

      const link: LinkPropertyHref[] = []
      if (isHomePage(i18n.locale.value, getPath(route))) {
        link.push(...getHrefLangLinkMetaForHomePage())
      }
      if (isTPPage(i18n.locale.value, getPath(route))) {
        link.push(...getHrefLangLinkMetaForTPPage())
      }

      if (props.page?.seo) {
        const seo = props.page.seo
        const frontendUrl = props.page.frontendUrl

        const facebookTags = [
          ...(seo.opengraphType
            ? [
                {
                  property: "og:type",
                  content: seo.opengraphType
                }
              ]
            : []),
          ...(seo.opengraphTitle
            ? [
                {
                  property: "og:title",
                  content: seo.opengraphTitle
                }
              ]
            : []),
          ...(seo.opengraphDescription
            ? [
                {
                  property: "og:description",
                  content: seo.opengraphDescription
                }
              ]
            : []),
          ...(frontendUrl
            ? [
                {
                  property: "og:url",
                  content: frontendUrl
                }
              ]
            : []),
          ...(seo.opengraphImage?.mediaDetails?.sizes?.length > 0
            ? [
                {
                  property: "og:image",
                  content: replaceUrlS3(
                    seo.opengraphImage.mediaDetails.sizes.find(s => s.name === "thumbnail")
                      ?.sourceUrl,
                    config.public
                  )
                }
              ]
            : [])
        ]

        const twitterTags = [
          ...(seo.twitterTitle
            ? [
                {
                  property: "twitter:title",
                  content: seo.twitterTitle
                }
              ]
            : []),
          ...(seo.twitterDescription
            ? [
                {
                  property: "twitter:description",
                  content: seo.twitterDescription
                }
              ]
            : []),
          ...(seo.twitterImage?.mediaDetails?.sizes?.length > 0
            ? [
                {
                  property: "twitter:image",
                  content: replaceUrlS3(
                    seo.twitterImage.mediaDetails.sizes.find(s => s.name === "thumbnail")
                      ?.sourceUrl,
                    config.public
                  )
                }
              ]
            : [])
        ]

        const robotsTag: RobotsMeta[] = []

        if (seo.metaRobotsNofollow) {
          robotsTag.push(seo.metaRobotsNofollow)
        }

        if (seo.metaRobotsNoindex) {
          robotsTag.push(seo.metaRobotsNoindex)
        }

        head.meta = [
          {
            hid: "description",
            name: "description",
            content: seo ? seo.metaDesc : ""
          },
          ...facebookTags,
          ...twitterTags
        ]

        if (robotsTag.length > 0) {
          head.meta.push({
            name: "robots",
            content: robotsTag.join(", ")
          })
        }

        if (!props.noSeo) {
          head.script.push({
            type: "application/ld+json",
            json: JSON.stringify(buildStructuredData(props.page))
          })
        }

        head.title = props.page?.title
      }

      if (
        [
          "PageLayoutAdvice",
          "PageLayoutBlog",
          "PageLayoutDefault",
          "PageLayoutRightSidebar"
        ].includes(props.layout) &&
        props.page?.featuredImage?.node
      ) {
        link.push({
          rel: "preload",
          as: "image",
          href: replaceUrlS3(props.page.featuredImage.node.sourceUrl, config.public)
        })
      }

      const canonical = props.page?.seo?.canonical
      const noindex = props.page?.seo?.metaRobotsNoindex === "noindex"
      const seoPage = !noindex

      if (seoPage && canonical && config.public.frontendHost) {
        link.push({
          rel: "canonical",
          href: canonical.includes(config.public.cmsHost)
            ? canonical
                .replace(config.public.cmsHost, config.public.frontendHost)
                .replace(/\/$/, "")
            : canonical.replace(/\/$/, "")
        })
      }

      head.link = [...link]
      return head
    })

    useHead(headConfig)

    return {
      config,
      i18n,
      route
    }
  },

  data(): ComponentData {
    return {
      layout: "",
      internalReviewsFilter: {}
    }
  },

  async fetch() {
    await this.setBreadcrumbs({
      breadcrumbs: this.page?.seo?.breadcrumbs
    })
  },

  computed: {
    ...mapGetters("auth", {
      nonce: GET_FW_TOKEN
    })
  },

  created() {
    const hasLatLongs =
      this.page &&
      this.page.location &&
      this.page.location.mapPinsLatitude &&
      this.page.location.mapPinsLongitude

    if (hasLatLongs) {
      this.setMapLocations(this.page.location)
      this.setLocationInfo(this.page.location)
    }

    if (this.page && this.page.pageInternalReviews) {
      this.setInternalReviews(this.page.pageInternalReviews)
    }

    this.layout = this.getLayoutFromTemplate()
    this.internalReviewsFilter = this.page.internalReviews
  },

  async mounted() {
    console.log("Hello, Nuxt 3 🐣")
    const query = this.route.query
    query.utm_media =
      query.utm_media || (this.page!.seo!.metaRobotsNoindex === "index" ? "seo" : "ppc")

    await this.authenticateAndUpdatePhone()
    await this.sendTrackingEvents()
    await this.useCreditCode()
  },

  methods: {
    replaceUrlS3,

    ...mapActions("pages", {
      setBreadcrumbs: SET_BREADCRUMBS
    }),
    ...mapActions("location", {
      setMapLocations: SET_MAP_LOCATIONS,
      setLocationInfo: SET_LOCATION_INFO
    }),
    ...mapActions("internal-reviews", {
      setInternalReviews: SET_INTERNAL_REVIEWS
    }),
    ...mapActions("auth", {
      setFwToken: SET_FW_TOKEN,
      setUser: SET_USER
    }),
    ...mapActions("phone-number", {
      setPhoneNumberValue: SET_PHONE_NUMBER_VALUE
    }),
    ...mapActions("credit-code", {
      setCreditCodeValue: SET_CREDIT_CODE_VALUE
    }),

    getLayoutFromTemplate(): string {
      let template =
        this.page &&
        this.page.template &&
        this.page.template.templateName
          .trim()
          .replace(/[-_\s]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ""))

      if (
        ![
          "Advice",
          "Article",
          "Avb",
          "Blank",
          "Blog",
          "Default",
          "RemovalsHub",
          "RightSidebar",
          "StaticDocument",
          "Rebrand"
        ].includes(template)
      ) {
        template = "Default"
      }

      return "PageLayout" + template
    },

    async authenticateAndUpdatePhone(): Promise<void> {
      const auth = new AuthenticationService(this.$axios, this.config.public)
      const profile = await auth.getProfile()
      if (profile && profile.fw_token) {
        await this.setFwToken(profile.fw_token)
        await this.updatePhoneNumber()
      }
      if (profile && profile.user) {
        await this.setUser(profile.user)
      }
    },

    async updatePhoneNumber(): Promise<void> {
      const phoneService = new PhoneNumberService(this.$axios, this.config.public)

      if (this.page && this.route && this.nonce) {
        const phoneNumber: PhoneNumberPair = await phoneService.updatePhoneNumber(
          this.page,
          this.route,
          this.nonce,
          this.i18n.locale.value
        )
        await this.setPhoneNumberValue(phoneNumber)

        const trackingData: CallUsPayload = {
          phoneNumber: phoneNumber.base,
          label: "lego_phone_number",
          action: "viewed"
        }
        if (phoneNumber.extension !== "") {
          // @ts-ignore
          trackingData.extension = phoneNumber.extension
        }
        this.$snowplow.trackCallUs(trackingData)
      }
    },

    async useCreditCode(): Promise<void> {
      const creditCodeService = new CreditCodeService(this.$axios, this.config.public)
      const creditCodeResponse = await creditCodeService.getCreditCodeValue(this.page, this.route)
      if (creditCodeResponse && creditCodeResponse.value) {
        this.setCreditCodeValue(parseInt(creditCodeResponse.value))
      }
    },

    async sendTrackingEvents(): Promise<void> {
      const trackingService = new TrackingService(this.$axios, this.config.public)
      const response = await trackingService.postTrackingEvent(
        { label: "LEGO - " + this.route.path },
        this.route,
        useCookie("PHPSESSID"),
        this.page,
        window
      )

      if (response && response.data && response.data.track_id) {
        this.$snowplow.trackAnyVanTrackId(response.data.track_id)
      }
    }
  }
})
</script>
